import React, { useEffect } from 'react';
import { NodeToolbar, Position } from 'reactflow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShuffle, faClockRotateLeft, faBullseye, faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { ToggleGroup, ToggleGroupItem } from '@/shadcn/components/ui/toggle-group';
import { NodeTemplate, NodeCategory } from '../../types';
import { createPlaceholderNode } from '../../utils/placeholderUtils';

interface NodeToolbarV2Props {
  nodeId: string;
  isVisible: boolean;
  onAddNode: (parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => void;
  onClose: () => void;
  isElseBranch?: boolean;
  position?: Position;
  offset?: number;
}

export const NodeToolbarV2: React.FC<NodeToolbarV2Props> = ({
  nodeId,
  isVisible,
  onAddNode,
  onClose,
  isElseBranch = false,
  position = Position.Right,
  offset = 15,
}) => {
  const handleAddDelay = () => {
    // Always create the actual node for inline insertion
    // The hasOppositeBranch information is just for context, not for changing behavior
    onAddNode(nodeId, {
      type: AutomationNodeType.DELAY,
      category: NodeCategory.UTILITY,
      displayName: 'Wait',
      description: 'Wait for a specified time',
      icon: 'clock',
      defaultMetadata: {}, // No default values - user must configure
      allowsChildren: true,
    }, isElseBranch, false);
    onClose();
  };

  const handleAddCondition = () => {
    // Always create the actual node for inline insertion
    // The hasOppositeBranch information is just for context, not for changing behavior
    onAddNode(nodeId, {
      type: AutomationNodeType.CONDITION,
      category: NodeCategory.LOGIC,
      displayName: 'Condition',
      description: 'Check a condition',
      icon: 'check',
      defaultMetadata: {},
      allowsChildren: true,
      maxChildren: 2,
    }, isElseBranch, false);
    onClose();
  };

  const handleAddAction = () => {
    // Always show "Select Action" placeholder instead of defaulting to SEND_EMAIL
    onAddNode(nodeId, {
      type: AutomationNodeType.ACTION,
      subType: undefined,
      category: NodeCategory.PLACEHOLDER,
      displayName: 'Select Action',
      description: 'Choose which action to perform',
      icon: 'action',
      defaultMetadata: { isActionPlaceholder: true },
      allowsChildren: false,
    }, isElseBranch, false);
    onClose();
  };

  const handleAddPath = () => {
    // "Add Path" creates a path-specific placeholder
    onAddNode(nodeId, createPlaceholderNode('path'), isElseBranch, true);
    onClose();
  };

  const allToggleItems = [
    {
      value: 'condition',
      icon: faShuffle,
      label: 'Condition',
      onClick: handleAddCondition,
    },
    {
      value: 'delay',
      icon: faClockRotateLeft,
      label: 'Delay',
      onClick: handleAddDelay,
    },
    {
      value: 'action',
      icon: faBullseye,
      label: 'Action',
      onClick: handleAddAction,
    },
    {
      value: 'addpath',
      icon: faCodeBranch,
      label: 'Add Path',
      onClick: handleAddPath,
    },
  ];

  const toggleItems = allToggleItems;

  const handleValueChange = (value: string) => {
    if (value) {
      const selectedItem = toggleItems.find((item) => item.value === value);
      if (selectedItem) {
        selectedItem.onClick();
      }
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <NodeToolbar
      nodeId={nodeId}
      isVisible={isVisible}
      position={position}
      offset={offset}
      align="end"
    >
      <div
        data-toolbar
        data-node-toolbar
        onClick={(e) => e.stopPropagation()}
      >
        <ToggleGroup
          type="single"
          value=""
          onValueChange={handleValueChange}
          className="inline-flex h-11 items-center justify-center px-1 py-0 bg-white rounded-xl border border-solid border-grey-2 shadow-lg"
        >
          {toggleItems.map((item) => (
            <ToggleGroupItem
              key={item.value}
              value={item.value}
              className="px-1 py-[7px] bg-white inline-flex flex-col items-center justify-center flex-[0_0_auto] rounded-lg data-[state=on]:bg-grey-2 hover:bg-grey-2/50"
            >
              <div className="inline-flex h-3 items-center justify-center">
                <FontAwesomeIcon icon={item.icon} className="w-3 h-3 text-grey-6" />
              </div>
              <div className="inline-flex items-center justify-center gap-2.5 px-0 py-[3px] flex-[0_0_auto]">
                <div className="w-fit mt-[-1.00px] font-normal text-grey-6 text-[11px] tracking-[0] leading-[11px] whitespace-nowrap">
                  {item.label}
                </div>
              </div>
            </ToggleGroupItem>
        ))}
        </ToggleGroup>
      </div>
    </NodeToolbar>
  );
};
